import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { crmService } from '../../services/crmService';
import NavigationBar from '../../components/ui/NavigationBar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import LeadFilters from './components/LeadFilters';
import LeadStageCard from './components/LeadStageCard';
import LeadTableRow from './components/LeadTableRow';
import LeadDetailModal from './components/LeadDetailModal';
import PipelineVisualization from './components/PipelineVisualization';
import { useNavigate } from 'react-router-dom';

const CRMLeadManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('table');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [realtimeStatus, setRealtimeStatus] = useState('connecting');
  const [filters, setFilters] = useState({
    search: '',
    source: 'all',
    interestLevel: 'all',
    budgetRange: 'all',
    followUpStatus: 'all',
    stage: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadLeads();
      
      // Setup real-time subscription
      const subscription = crmService?.subscribeToLeads(user?.id, (payload) => {
        setRealtimeStatus('connected');
        
        if (payload?.eventType === 'INSERT') {
          setLeads(prev => [{
            id: payload?.new?.id,
            advisorId: payload?.new?.advisor_id,
            nombre: payload?.new?.nombre,
            email: payload?.new?.email,
            telefono: payload?.new?.telefono,
            fuente: payload?.new?.fuente,
            proyectoInteres: payload?.new?.proyecto_interes,
            presupuesto: payload?.new?.presupuesto,
            stage: payload?.new?.stage,
            nivelInteres: payload?.new?.nivel_interes,
            ultimaActividad: payload?.new?.ultima_actividad,
            proximoSeguimiento: payload?.new?.proximo_seguimiento,
            notas: payload?.new?.notas,
            createdAt: payload?.new?.created_at,
            updatedAt: payload?.new?.updated_at
          }, ...prev]);
        } else if (payload?.eventType === 'UPDATE') {
          setLeads(prev => prev?.map(lead => 
            lead?.id === payload?.new?.id ? {
              ...lead,
              nombre: payload?.new?.nombre,
              email: payload?.new?.email,
              telefono: payload?.new?.telefono,
              fuente: payload?.new?.fuente,
              proyectoInteres: payload?.new?.proyecto_interes,
              presupuesto: payload?.new?.presupuesto,
              stage: payload?.new?.stage,
              nivelInteres: payload?.new?.nivel_interes,
              ultimaActividad: payload?.new?.ultima_actividad,
              proximoSeguimiento: payload?.new?.proximo_seguimiento,
              notas: payload?.new?.notas,
              updatedAt: payload?.new?.updated_at
            } : lead
          ));
        } else if (payload?.eventType === 'DELETE') {
          setLeads(prev => prev?.filter(lead => lead?.id !== payload?.old?.id));
        }
      });

      return () => {
        crmService?.unsubscribeFromLeads(subscription);
      };
    }
  }, [user]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      setError('');
      const leadsData = await crmService?.getLeads(user?.id);
      setLeads(leadsData);
    } catch (error) {
      console.error('Error loading leads:', error);
      setError('Error al cargar los leads');
    } finally {
      setLoading(false);
    }
  };

  const filteredLeads = useMemo(() => {
    return leads?.filter(lead => {
      const matchesSearch = !filters?.search || 
        lead?.nombre?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        lead?.email?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        lead?.telefono?.includes(filters?.search);
      
      const matchesSource = filters?.source === 'all' || lead?.fuente === filters?.source;
      const matchesInterest = filters?.interestLevel === 'all' || lead?.nivelInteres === filters?.interestLevel;
      const matchesStage = filters?.stage === 'all' || lead?.stage === filters?.stage;
      
      const matchesBudget = filters?.budgetRange === 'all' || (() => {
        const budget = lead?.presupuesto || 0;
        switch(filters?.budgetRange) {
          case 'low': return budget < 300000;
          case 'medium': return budget >= 300000 && budget < 500000;
          case 'high': return budget >= 500000;
          default: return true;
        }
      })();

      return matchesSearch && matchesSource && matchesInterest && matchesBudget && matchesStage;
    }) || [];
  }, [leads, filters]);

  const stageGroups = useMemo(() => {
    const stages = {
      new: { label: 'Nuevo', leads: [], color: 'var(--color-accent)' },
      contacted: { label: 'Contactado', leads: [], color: '#3B82F6' },
      qualified: { label: 'Calificado', leads: [], color: 'var(--color-primary)' },
      proposal: { label: 'Propuesta', leads: [], color: '#F59E0B' },
      negotiation: { label: 'Negociación', leads: [], color: '#8B5CF6' },
      closed_won: { label: 'Ganado', leads: [], color: 'var(--color-success)' },
      closed_lost: { label: 'Perdido', leads: [], color: 'var(--color-error)' }
    };

    filteredLeads?.forEach(lead => {
      if (stages?.[lead?.stage]) {
        stages?.[lead?.stage]?.leads?.push(lead);
      }
    });

    return stages;
  }, [filteredLeads]);

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedLead(null);
  };

  const handleUpdateLead = async (leadId, updates) => {
    try {
      await crmService?.updateLead(leadId, updates);
      await loadLeads();
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      source: 'all',
      interestLevel: 'all',
      budgetRange: 'all',
      followUpStatus: 'all',
      stage: 'all'
    });
  };

  const handleUpdateStatus = async (leadId, newStatus) => {
    await handleUpdateLead(leadId, { stage: newStatus });
  };

  const handleAddNote = async (leadId, note) => {
    await handleUpdateLead(leadId, { notes: note });
  };

  const handleSaveLead = async (leadId, updates) => {
    await handleUpdateLead(leadId, updates);
    handleCloseModal();
  };

  const handleStageChange = async (leadId, newStage) => {
    await handleUpdateLead(leadId, { stage: newStage });
  };

  const handleExportLeads = () => {
    // TODO: Implement export functionality
    console.log('Exporting leads...');
  };

  const breadcrumbItems = [
    { label: 'Inicio', path: '/landing-page' },
    { label: 'CRM', path: '/crm-lead-management' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Cargando leads...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 mt-16 sm:mt-20">
        <BreadcrumbTrail items={breadcrumbItems} />
        
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                Gestión de Leads CRM
              </h1>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
                  Administra y da seguimiento a tus clientes potenciales
                </p>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    realtimeStatus === 'connected' ? 'bg-success animate-pulse' :
                    realtimeStatus === 'connecting'? 'bg-warning animate-pulse' : 'bg-destructive'
                  }`} />
                  <span className="text-xs text-muted-foreground">
                    {realtimeStatus === 'connected' ? 'En vivo' :
                     realtimeStatus === 'connecting' ? 'Conectando...' :
                     'Desconectado'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Buttons - Mobile optimized */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button
                variant="outline"
                iconName={viewMode === 'table' ? 'LayoutGrid' : 'Table'}
                onClick={() => setViewMode(viewMode === 'table' ? 'kanban' : 'table')}
                className="text-xs sm:text-sm"
              >
                {viewMode === 'table' ? 'Vista Kanban' : 'Vista Tabla'}
              </Button>
              <Button
                variant="outline"
                iconName="Filter"
                onClick={() => setShowFilters(!showFilters)}
                className="text-xs sm:text-sm"
              >
                Filtros {Object.values(filters)?.filter(v => v !== 'all' && v !== '')?.length > 0 && `(${Object.values(filters)?.filter(v => v !== 'all' && v !== '')?.length})`}
              </Button>
              <Button
                variant="outline"
                iconName="Download"
                onClick={handleExportLeads}
                className="text-xs sm:text-sm"
              >
                Exportar
              </Button>
              <Button
                variant="outline"
                iconName="BarChart3"
                onClick={() => navigate('/advanced-analytics-custom-dashboard-builder')}
                className="text-xs sm:text-sm"
              >
                Analíticas
              </Button>
            </div>
          </div>
        </div>

        {/* Filters Panel - Collapsible on mobile */}
        {showFilters && (
          <div className="mb-4 sm:mb-6">
            <LeadFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />
          </div>
        )}

        {/* Stats Bar - Mobile optimized */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <div className="card p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-muted-foreground mb-1">Total Leads</p>
            <p className="text-lg sm:text-2xl font-bold text-foreground">{filteredLeads?.length}</p>
          </div>
          <div className="card p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-muted-foreground mb-1">Nuevos</p>
            <p className="text-lg sm:text-2xl font-bold text-accent">{stageGroups?.new?.leads?.length || 0}</p>
          </div>
          <div className="card p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-muted-foreground mb-1">Calificados</p>
            <p className="text-lg sm:text-2xl font-bold text-primary">{stageGroups?.qualified?.leads?.length || 0}</p>
          </div>
          <div className="card p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-muted-foreground mb-1">Ganados</p>
            <p className="text-lg sm:text-2xl font-bold text-success">{stageGroups?.closed_won?.leads?.length || 0}</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg">
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        <div className="mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            <div className="bg-card p-4 rounded-xl border border-border">
              <div className="flex items-center justify-between mb-2">
                <Icon name="Users" size={20} className="text-primary" />
                <span className="text-xs font-medium text-muted-foreground">Total</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{filteredLeads?.length}</p>
            </div>
            <div className="bg-card p-4 rounded-xl border border-border">
              <div className="flex items-center justify-between mb-2">
                <Icon name="Flame" size={20} className="text-error" />
                <span className="text-xs font-medium text-muted-foreground">Calientes</span>
              </div>
              <p className="text-2xl font-bold text-foreground">
                {filteredLeads?.filter(l => l?.nivelInteres === 'hot')?.length}
              </p>
            </div>
            <div className="bg-card p-4 rounded-xl border border-border">
              <div className="flex items-center justify-between mb-2">
                <Icon name="Target" size={20} className="text-warning" />
                <span className="text-xs font-medium text-muted-foreground">Tibios</span>
              </div>
              <p className="text-2xl font-bold text-foreground">
                {filteredLeads?.filter(l => l?.nivelInteres === 'warm')?.length}
              </p>
            </div>
            <div className="bg-card p-4 rounded-xl border border-border">
              <div className="flex items-center justify-between mb-2">
                <Icon name="CheckCircle" size={20} className="text-success" />
                <span className="text-xs font-medium text-muted-foreground">Ganados</span>
              </div>
              <p className="text-2xl font-bold text-foreground">
                {filteredLeads?.filter(l => l?.stage === 'closed_won')?.length}
              </p>
            </div>
            <div className="bg-card p-4 rounded-xl border border-border col-span-2 sm:col-span-1">
              <div className="flex items-center justify-between mb-2">
                <Icon name="DollarSign" size={20} className="text-primary" />
                <span className="text-xs font-medium text-muted-foreground">Pipeline</span>
              </div>
              <p className="text-lg sm:text-2xl font-bold text-foreground">
                S/ {(filteredLeads?.reduce((sum, l) => sum + (l?.presupuesto || 0), 0) / 1000)?.toFixed(0)}K
              </p>
            </div>
          </div>
        </div>

        {viewMode === 'table' && (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Nombre</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Contacto</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Fuente</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Presupuesto</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Estado</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Interés</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredLeads?.length > 0 ? (
                    filteredLeads?.map((lead) => (
                      <LeadTableRow
                        key={lead?.id}
                        lead={lead}
                        onViewDetails={handleLeadClick}
                        onUpdateStatus={handleUpdateStatus}
                        onAddNote={handleAddNote}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center">
                        <Icon name="Inbox" size={48} className="mx-auto mb-4 text-muted-foreground" />
                        <p className="text-muted-foreground">No se encontraron leads</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {viewMode === 'kanban' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            {Object.entries(stageGroups)?.map(([stageKey, stageData]) => (
              <LeadStageCard
                key={stageKey}
                stage={stageKey}
                label={stageData?.label}
                leads={stageData?.leads}
                color={stageData?.color}
                count={stageData?.leads?.length || 0}
                percentage={filteredLeads?.length > 0 ? Math.round((stageData?.leads?.length / filteredLeads?.length) * 100) : 0}
                icon={stageKey === 'new' ? 'UserPlus' : stageKey === 'contacted' ? 'Phone' : stageKey === 'qualified' ? 'CheckCircle' : stageKey === 'proposal' ? 'FileText' : stageKey === 'negotiation' ? 'MessageSquare' : stageKey === 'closed_won' ? 'ThumbsUp' : 'ThumbsDown'}
                onLeadClick={handleLeadClick}
              />
            ))}
          </div>
        )}

        {viewMode === 'pipeline' && (
          <PipelineVisualization 
            stageGroups={stageGroups}
            leads={filteredLeads}
            onStageChange={handleStageChange}
          />
        )}

        {showDetailModal && selectedLead && (
          <LeadDetailModal
            lead={selectedLead}
            onClose={handleCloseModal}
            onUpdate={handleUpdateLead}
            onSave={handleSaveLead}
          />
        )}
      </div>
    </div>
  );
};

export default CRMLeadManagement;